import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { getCurrentLocation } from "frontend/services/monday-api-service";
import tracking from "frontend/tracking";
import { nanoid } from "nanoid";
import consts from "shared/consts";
import { isClient } from "utils/window-utils";

export function mondayURL(slug: string, path: string) {
  return `${process.env.MONDAY_SCHEME}${slug}.${process.env.MONDAY_DOMAIN}/${path}`;
}

const isHEX = (h: string) => !isNaN(Number.parseInt(h, 16));
const RFC4122Length = [8, 4, 4, 4, 12];

export function isGuidValid(guid: string) {
  if (!guid) {
    return false;
  }
  // Must have 36 Chars per codepoint (32 + 4 dashes)
  const codePointLenght = [...guid].length;
  if (codePointLenght !== 36) return false;

  // Must have 5 groups
  const groups = guid.split("-");
  if (groups.length !== 5) return false;

  // RFC defines 5 groups of 8-4-4-4-12 codepoints chars each
  const lengthMatch = groups.map((gr) => [...gr].length).every((length_, index) => length_ === RFC4122Length[index]);
  if (!lengthMatch) return false;

  // Finally join groups (without the "-" separators and check for each char is HEX)
  return [...groups.join("")].every(isHEX);
}

function getSubdomain(url: string): string | null {
  try {
    const { hostname } = new URL(url);
    const parts = hostname.split(".");

    // Check if the hostname has a subdomain (i.e., more than 2 parts)
    if (parts.length > 2) {
      return parts.slice(0, -2).join("."); // Join the subdomain parts
    }
    return null; // No subdomain found
  } catch (error) {
    console.error("Invalid URL:", error);
    return null; // Return null if the URL is invalid
  }
}

export async function addAdditionalParamsToCIMUrl(url: string) {
  if (!isCIM()) {
    return "";
  }
  const currentLocation = await getCurrentLocation();
  const subdomain = getSubdomain(currentLocation);
  switch (url) {
    case process.env.MONDAY_AUTH_REDIRECT_URL: {
      return `&subdomain=${subdomain}`;
    }
    default: {
      return "";
    }
  }
}

// to make it more secure, we keep the path to go back to in a cookie.
// the cookie name is a unique hash passed as a `state` param.
// when the callback of this url is called, we check that a cookie with that name exist, and if true
// we redirect to that path.
// it the cookie doesn't exist, it means that this client didn't generate that url and is invalid.
export async function generateRedirectURL(url: string = "", keepPath = true, params: Record<string, string> = {}) {
  const hashedCookieName = nanoid();
  const currentPath = keepPath
    ? Object.entries(params).reduce((acc, [key, value]) => {
        acc += encodeURIComponent(`#${key}=${value}`);
        return acc;
      }, window.location.pathname)
    : "/";
  const expirationDate = new Date();
  expirationDate.setHours(expirationDate.getHours() + 1);
  document.cookie = `${hashedCookieName}=${currentPath}; path=/; samesite=strict; secure; expires=${expirationDate.toUTCString()}`;
  const paramSign = url.includes("?") ? "&" : "?";
  const cimAdditionalParams = await addAdditionalParamsToCIMUrl(url);
  let redirectURL = `${url}${paramSign}state=${hashedCookieName}${cimAdditionalParams}`;

  // Add query string to redirect URL
  const [_, queryString] = currentPath.split("%23"); // split by #
  if (queryString) {
    redirectURL += `#${queryString.replaceAll("&", "#")}`;
  }
  return redirectURL;
}

export function getPathFromState(state?: string, currentUrlFullPath?: string) {
  if (!state) {
    throw new Error("No state provided");
  }
  const cookie = document.cookie
    .split(";")
    .find((c) => c.trim().startsWith(state))
    ?.split("=")[1];
  if (!cookie) {
    if (isCIM()) {
      tracking.trackAnalyticsEvent("canvas_monday_path_no_cookie");
      // %3D is = url encoded
      if (currentUrlFullPath?.includes("create_integration%3Dmonday-tasks")) {
        return `${process.env.CANVAS_IN_MONDAY_BOARD_PATH}#create_integration=monday-tasks`;
      } else {
        return `${process.env.CANVAS_IN_MONDAY_BOARD_PATH}`;
      }
    }
    return "/";
  }
  // remove cookie
  document.cookie = `${state}=; path=/; samesite=strict; secure; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  return decodeURIComponent(cookie);
}

export const getDocumentIdFromURL = () => {
  return window.location.pathname.split("/d").at(-1)?.replace("/", "") ?? "";
};

export function isInIframe() {
  try {
    if (!isClient()) {
      return false;
    }
    return window?.self !== window?.top;
  } catch (error) {
    console.error("isInIframe function error", error);
    return false;
  }
}

export function isCIM() {
  if (!isInIframe()) {
    return false;
  }

  const sessionToken = sessionStorage.getItem(consts.MONDAY_APP_SESSION_TOKEN);
  if (sessionToken) {
    return true;
  }

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has("sessionToken");
}

export const isJWT = (token: string | null): boolean => {
  if (!token) return false;
  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.match(/^[A-Za-z0-9_-]+$/));
};

export function getIframeSessionToken(): string | null {
  if (!isCIM()) {
    return null;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const urlSessionToken = urlParams.get("sessionToken");
  if (urlSessionToken && isJWT(urlSessionToken)) {
    sessionStorage.setItem(consts.MONDAY_APP_SESSION_TOKEN, urlSessionToken);
    return urlSessionToken;
  }

  return sessionStorage.getItem(consts.MONDAY_APP_SESSION_TOKEN);
}

export function getIsMobile(userAgent: string | undefined) {
  if (!userAgent) {
    return false;
  }
  return Boolean(
    /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(userAgent) ||
      /\b(Android|Windows Phone|iPad|iPod)\b/i.test(userAgent)
  );
}

export function getSafeUrlPathname(url: string) {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}
export const extractCIMUrlParams = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const boardId = urlParams.get("boardId");
  const instanceId = urlParams.get("instanceId");
  const boardViewId = urlParams.get("boardViewId");
  return { boardId, instanceId, boardViewId };
};

export const downloadFileUsingBlob = async (blob: Blob, filename: string) => {
  const bloblUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = bloblUrl;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(bloblUrl);
};

export const downloadFile = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
};

export const useIsCIMFooterEnabled = () => {
  const isCIMFooterEnabled = useFeatureFlag("cim-footer");
  return isCIM() && isCIMFooterEnabled;
};

const checkCimURLParams = () => {
  if (isClient()) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("cim") === "true";
  }
  return false;
};

export const useIsCIMReorderEnabled = () => {
  const isCIMReorderEnabled = useFeatureFlag("cim-reorder");
  if (checkCimURLParams() && isCIMReorderEnabled) {
    return true;
  }
  return isCIM() && isCIMReorderEnabled;
};

export function decodeJwtResponse(token: string) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");

    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

    const jsonPayload = window.atob(paddedBase64);
    return JSON.parse(jsonPayload);
  } catch {
    throw new Error("Failed to decode Google authentication token");
  }
}
